/**
 * LauncherMark — the branded "face" shared by BOTH agents' launchers (FLO-585).
 *
 * One chain, one place: site logo → self-hosted favicon → letter-mark on the
 * site accent. The Floe mark never appears here, and failed image sources
 * degrade without painting a broken image.
 *
 * Extracted because the website agent's pill and the onboarding orb had drifted
 * apart — the orb still painted our own SVG over a hardcoded dark gradient while
 * the pill already rendered the customer's brand. Two copies of "logo → favicon
 * → letter on accent" is exactly how they drifted, so the chain lives in one
 * component.
 */

import React, { useMemo, useState } from 'react';

const MIN_MARK_CONTRAST = 3;
const NEUTRAL_MARK_BACKGROUND = '#77716D';

function relativeLuminance(hex: string): number {
  const channels = [1, 3, 5].map((offset) => {
    const channel = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/**
 * Pick a tile colour that keeps the two common transparent-mark extremes
 * (pure white and pure black) at or above WCAG's 3:1 non-text contrast target.
 *
 * The configured accent remains the first choice, so the mark still feels like
 * the customer's brand. Very light, very dark, or malformed accents use a warm
 * mid-neutral instead; no single unknown background can protect every colour,
 * but this avoids exchanging the white-on-white bug for black-on-dark.
 */
export function brandMarkBackground(accent?: string): string {
  const normalized = accent?.trim() || '';
  if (!/^#[0-9a-f]{6}$/i.test(normalized)) return NEUTRAL_MARK_BACKGROUND;

  const luminance = relativeLuminance(normalized);
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;

  return contrastWithWhite >= MIN_MARK_CONTRAST &&
    contrastWithBlack >= MIN_MARK_CONTRAST
    ? normalized
    : NEUTRAL_MARK_BACKGROUND;
}

/**
 * Ordered, usable brand-image candidates. The explicit logo wins, but the
 * self-hosted favicon is still valuable when a site only has one or when the
 * primary image later becomes unavailable. Deduping prevents a broken shared
 * URL from needlessly retrying itself before we show the letter-mark.
 */
export function brandImageSources(
  siteLogoUrl?: string,
  siteFaviconUrl?: string,
): string[] {
  return [siteLogoUrl, siteFaviconUrl].reduce<string[]>((sources, value) => {
    const url = value?.trim();
    if (url && !sources.includes(url)) sources.push(url);
    return sources;
  }, []);
}

export const LauncherMark: React.FC<{
  /** Site logo. A failed logo advances to `siteFaviconUrl`, then the letter. */
  siteLogoUrl?: string;
  /** Self-hosted favicon: the durable second branding tier. */
  siteFaviconUrl?: string;
  /** Fallback letter (first char of the site name, upper-cased by the caller). */
  letter: string;
  /** Brand accent (#RRGGBB) for the letter-mark circle. */
  accent: string;
  /** Diameter in px. */
  size?: number;
  /** Override for surfaces whose existing fallback typography is smaller. */
  letterSize?: number;
}> = ({ siteLogoUrl, siteFaviconUrl, letter, accent, size = 30, letterSize }) => {
  const sources = useMemo(
    () => brandImageSources(siteLogoUrl, siteFaviconUrl),
    [siteLogoUrl, siteFaviconUrl],
  );
  // Record failed URLs rather than a positional index. A fresh URL is therefore
  // eligible on the SAME render that receives it, never briefly skipped by an
  // index left over from a previous logo/favicon pair.
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const src = sources.find((source) => !failedSources.includes(source));

  if (src) {
    const inset = Math.max(2, Math.round(size * 0.1));
    return (
      <img
        key={src}
        src={src}
        alt=""
        width={size}
        height={size}
        onError={() =>
          setFailedSources((failed) =>
            failed.includes(src) ? failed : [...failed, src],
          )
        }
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'contain',
          boxSizing: 'border-box',
          padding: inset,
          backgroundColor: brandMarkBackground(accent),
          boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
          flexShrink: 0,
          display: 'block',
        }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: accent,
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: letterSize ?? Math.round(size * 0.5),
        fontWeight: 700,
      }}
    >
      {letter}
    </span>
  );
};
